import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import appuserapi from "@/api/appuserapi"
import officeapi from "@/api/officeapi"
import { getTaskBoxIndex } from "@/functions/office"
import { createResultSuggestions } from "@/functions/resultSuggestion.js"
import SessionStorageService from "@/services/SessionStorageService";
import { ItemTag } from '@/models/share.model'
import { updateI18nLocale } from '@/i18n'
import { PersonLightResponse, TaskBoxesResponse, TaskResponse, TaskApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import PersonService from '@/services/PersonService'

const taskApi = new TaskApi(medServerApiConfig);

export const useAppUserStore = defineStore('appUser', () => {
  const healthProDelegate = ref<PersonLightResponse[]>(null); 
  const refreshCalendar = ref<boolean>(false);
  const refreshHealthProCalendar = ref<boolean>(false);
  const refreshHealthProInstitute = ref<boolean>(false);
  const reactiveCurrentAgendaType = ref(getCurrentAgendaType() ?? '1');

  const setRefreshCalendar = (newValue: boolean) => {
    refreshCalendar.value = newValue;
  }

  const setRefreshHealthProCalendar = (newValue: boolean) => {
    refreshHealthProCalendar.value = newValue;
  }

  const setRefreshHealthProInstitute = (newValue: boolean) => {
    refreshHealthProInstitute.value = newValue;
  }

  const refreshOnlyEvent = ref<boolean>(false);

  const setRefreshOnlyEvent = (newValue: boolean) => {
    refreshOnlyEvent.value = newValue;
  }

  const refreshAccounting = ref<boolean>(false);

  const setRefreshAccounting = (newValue: boolean) => {
    refreshAccounting.value = newValue;
  }

  const commonAlerts = ref(null)
  async function setCommonAlerts() {
    const alerts = await appuserapi.getRefCommonAlerts()
    commonAlerts.value = alerts
  }

  const appUserDocumentGroups = ref(null)
  const currentAppUserData = ref(null)
  const cachedResultSuggestions = ref(null)
  async function setCurrentAppUserData() {
    const currentAppUser = await appuserapi.getCurrentAppUser()
    //currentAppUser + healthGroupMemberships
    currentAppUser.healthGroupMemberships.sort((a: any, b: any) => {
      return a.instituteWithRole.instituteShortName.localeCompare(b.instituteWithRole.instituteShortName)
    })
    currentAppUserData.value = currentAppUser
    //menuLanguage
    const userLocale = currentAppUser?.preferences.menuLanguageIso || "fr";
    updateI18nLocale(userLocale)
    //cachedResultSuggestions
    updateCachedResultSuggestions()
    //documentGroups

    //Check if default preference already create
    if (currentAppUser.preferences.bioPrescriptions.general.elements.length === 0) {
      appuserapi.createDefaultReferences().then(() => {
        //refresh current AppUser
        appuserapi.getCurrentAppUser().then((currentAppUser) => {
          currentAppUser.healthGroupMemberships.sort((a: any, b: any) => {
            return a.instituteWithRole.instituteShortName.localeCompare(b.instituteWithRole.instituteShortName)
          })
          currentAppUserData.value = currentAppUser;
        })
      } );
    }

    appUserDocumentGroups.value = await appuserapi.allDocumentGroupsToDto()
  }
  function updateCachedResultSuggestions() {
    const sctLanguage = currentAppUserData.value?.preferences.sctLanguage;
    const resultSuggestions = currentAppUserData.value?.preferences.resultSuggestions;
    cachedResultSuggestions.value = createResultSuggestions(resultSuggestions, sctLanguage);
  }  

  const currentHealthGroupMembership = ref(null)  

  function setCurrentHealthGroupMembership(instituteWithRole: any) {
    currentHealthGroupMembership.value.pwr = instituteWithRole;
    SessionStorageService.setItem("currentHealthGroupMembership", JSON.stringify(currentHealthGroupMembership.value));
  }

  async function getCurrentHealthGroupMembership() {
    if (SessionStorageService.getItem("currentHealthGroupMembership")) {
        currentHealthGroupMembership.value = JSON.parse(SessionStorageService.getItem("currentHealthGroupMembership"));
        return currentHealthGroupMembership.value ;
    } else {
        currentHealthGroupMembership.value = await appuserapi.getCurrentHealthGroupMembership();
        SessionStorageService.setItem("currentHealthGroupMembership", JSON.stringify(currentHealthGroupMembership.value));
        return currentHealthGroupMembership.value ;
    }
  }

  /**
   * Update the current health group membership data from the backend
   */
  async function updateCurrentHealthGroupMembership(){
    currentHealthGroupMembership.value = await appuserapi.getCurrentHealthGroupMembership();
    SessionStorageService.setItem("currentHealthGroupMembership", JSON.stringify(currentHealthGroupMembership.value));
    return currentHealthGroupMembership.value ;
  }

  function updateCurrentAgendaType(currentAgendaTypeIndex : number) {
    reactiveCurrentAgendaType.value = String(currentAgendaTypeIndex);
    SessionStorageService.setItem("currentAgendaType", JSON.stringify(currentAgendaTypeIndex));
  }

  function updateCurrentAgendaDate(currentAgendaDate : string) {
    SessionStorageService.setItem("currentAgendaDate", currentAgendaDate);
  }

  function updateCurrentAgendaHealthPro(currentAgendaHealthPros : ItemTag[]) {
    SessionStorageService.setItem("currentAgendaHealthPros", JSON.stringify(currentAgendaHealthPros));
  }

  function getCurrentAgendaType() {
    return SessionStorageService.getItem("currentAgendaType");
  }

  function getCurrentAgendaDate() {
    if(SessionStorageService.getItem("currentAgendaDate") !== null){
      return SessionStorageService.getItem("currentAgendaDate");
    }
    
    return null
  }

  function getCurrentAgendaHealthPro() {
    return JSON.parse(SessionStorageService.getItem("currentAgendaHealthPros"));
  }

  const getPortalModuleAccess = (): boolean => {
    return currentAppUserData.value?.preferences.portalModuleAccess;
  } 

  function getCurrentInstituteWithRole() {
    return currentHealthGroupMembership.value?.instituteWithRole;
  }

  const currentAppUserEntries = ref(null)
  async function setCurrentAppUserEntries(requestedIsoDateTime : string) {
    const currentDate = new Date()
    requestedIsoDateTime = currentDate.toISOString()    
    const tempEntryList = await officeapi.getCurrentHproEntries(requestedIsoDateTime)
    tempEntryList.sort((a: any, b: any) => {
      return new Date(b.entryStart).getTime() - new Date(a.entryStart).getTime() || new Date(b.updated).getTime() - new Date(a.updated).getTime()
    })
    currentAppUserEntries.value = tempEntryList
  }
  
  //Tasks & TaskBoxes
  const taskList = ref<TaskResponse[]>([]);
  async function setTaskList (taskListToAdd: TaskResponse[]) {
    taskList.value.push(...taskListToAdd);
    taskList.value.forEach((task: any) => {
      task.taskNotes.sort((a: any, b: any) => new Date(a.updated).getTime() - new Date(b.updated).getTime())
    });
  }
  
  const taskBoxList = ref([])
  const taskBoxTwoPersonList = ref([])
  const taskBoxAssigneeList = ref([])
  const taskBoxNonAssigneeList = ref([])  

  async function setTaskBoxList() {
    let initTaskStore = false;

    await taskApi.getTaskBoxes({justTwoPersonChannel: false}).then((result: TaskBoxesResponse[]) => {
      setTaskBoxByAssigneeValue(result);
      initTaskStore = !result || result.length === 0;
    });

    await taskApi.getTaskBoxes({justTwoPersonChannel: true}).then((result: TaskBoxesResponse[]) => {
      setTaskBoxTwoPerson(result);
      initTaskStore = initTaskStore && (!result || result.length === 0);
    });

    if (initTaskStore) {
      initTaskBox();
    }
  }

  function setTaskBoxByAssigneeValue(taskBoxesResponse: TaskBoxesResponse[]) {
    if (!taskBoxesResponse || !taskBoxesResponse?.length) {
      taskBoxAssigneeList.value = [];
      taskBoxNonAssigneeList.value = [];
    } else {
      taskBoxAssigneeList.value = taskBoxesResponse.filter((taskBox: TaskBoxesResponse) => taskBox.isAssignee).sort((a, b) => getTaskBoxIndex(a) - getTaskBoxIndex(b) || a.taskBoxTitle.localeCompare(b.taskBoxTitle) || a.taskBoxId.localeCompare(b.taskBoxId));
      taskBoxNonAssigneeList.value = taskBoxesResponse.filter((taskBox: TaskBoxesResponse) => !taskBox.isAssignee).sort((a, b) => getTaskBoxIndex(a) - getTaskBoxIndex(b) || a.taskBoxTitle.localeCompare(b.taskBoxTitle) || a.taskBoxId.localeCompare(b.taskBoxId));
      taskBoxList.value.push(...taskBoxesResponse);
    }
  }

  function setTaskBoxTwoPerson(taskBoxesResponseTwoPerson: TaskBoxesResponse[]) {
    if (!taskBoxesResponseTwoPerson || !taskBoxesResponseTwoPerson?.length) {
      taskBoxTwoPersonList.value = [];
    } else {
      taskBoxTwoPersonList.value = taskBoxesResponseTwoPerson.filter((taskBox: TaskBoxesResponse) => taskBox.isAssignee).sort((a, b) => getTaskBoxIndex(a) - getTaskBoxIndex(b) || a.taskBoxTitle.localeCompare(b.taskBoxTitle) || a.taskBoxId.localeCompare(b.taskBoxId));
      taskBoxList.value.push(...taskBoxesResponseTwoPerson);
    }
  }

  function initTaskBox() {
    taskList.value = [];
    taskBoxList.value = [];
    taskBoxTwoPersonList.value = [];
    taskBoxAssigneeList.value = [];
    taskBoxNonAssigneeList.value = [];
  }

  const currHealthProPwrUuid = computed(() =>
    currentHealthGroupMembership.value?.pwr?.personWithRoleId ?? null
  )

  function getHealthProNameWithTitle() {
    return currentHealthGroupMembership.value?.pwr ? 
          [currentHealthGroupMembership.value.pwr.title, PersonService.formatName(currentHealthGroupMembership.value.pwr.lastName, currentHealthGroupMembership.value.pwr.firstName)].filter(Boolean).join(' ')
          : undefined;
  }

  //Return
  return {
    commonAlerts,
    currentHealthGroupMembership,
    healthProDelegate,
    taskList,
    taskBoxList,
    taskBoxTwoPersonList,
    taskBoxAssigneeList,
    taskBoxNonAssigneeList,
    cachedResultSuggestions,
    currentAppUserData,
    appUserDocumentGroups,
    currentAppUserEntries,
    refreshCalendar,
    refreshHealthProCalendar,
    refreshAccounting,
    refreshOnlyEvent,
    reactiveCurrentAgendaType,
    currHealthProPwrUuid,
    setCommonAlerts,
    setCurrentHealthGroupMembership,
    getCurrentHealthGroupMembership,
    getCurrentInstituteWithRole,
    setTaskList,
    setTaskBoxList,
    setTaskBoxByAssigneeValue,
    setTaskBoxTwoPerson,
    initTaskBox,
    setCurrentAppUserData,
    updateCachedResultSuggestions,
    setCurrentAppUserEntries,
    updateCurrentHealthGroupMembership,
    setRefreshCalendar,
    setRefreshHealthProCalendar,
    setRefreshAccounting,
    setRefreshOnlyEvent,
    updateCurrentAgendaType,
    updateCurrentAgendaDate,
    updateCurrentAgendaHealthPro,
    getCurrentAgendaType,
    getCurrentAgendaDate,
    getCurrentAgendaHealthPro,
    setRefreshHealthProInstitute,
    refreshHealthProInstitute,
    getPortalModuleAccess,
    getHealthProNameWithTitle
  }
})
